import { Stack, Dialog, Button, Typography, DialogContent, DialogActions } from '@mui/material';
import { PriceUnit, PositionTxType, UserTransactionPaymentType } from 'src/type/position';
import { TransactionType, TransactionStatus } from 'src/type/transaction';
import Label from 'src/components/label';
import { fNumber } from 'src/utils/format-number';
import { fDate } from 'src/utils/format-time';
import InfoGrid from 'src/components/ui-kit/info-grid';
import ImageWithPreview from 'src/components/ui-kit/image-with-preview';
import { txAction, statusAction, txStatusLabel, paymentTypeLabel } from './utils';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  tx: PositionTxType;
};

export default function PositionViewDialog({ open, onClose, tx }: Props) {
  const getInfo = () => {
    if (!tx) return [];

    // console.log(tx);

    const {
      status,
      updatedAt,
      approvedByUser,
      createdAt,
      user,
      amount,
      paidAt,
      note,
      // closePositionPricePerUnit,
      fee,
      paymentImageUrl,
      paymentType,
      remainGoldAmount,
      paymentVoucherNo,
      paymentReceiptNo,
      unitName,
      ticketNumber,
      ticketCreatedAt,
      pricePerUnit,
      priceDifferencePerUnit,
    } = tx;

    // Check if product is silver
    const isSilverProduct = /เงิน|silver/i.test(tx.product.name || '');

    // Use correct price field based on PriceUnit enum
    // For silver products, price is stored in:
    // - pricePerKg (per kilogram) - no conversion needed, already correct unit
    // - pricePerGram (per gram) - needs conversion to kilogram (multiply by 1000)
    // - pricePerBaht (per baht) - needs conversion to kilogram (multiply by 65.6)
    const displayPricePerUnit = (() => {
      if (isSilverProduct) {
        // For silver products, determine conversion based on PriceUnit
        if (tx.priceUnit === PriceUnit.PRICE_PER_KG) {
          // Already per kilogram, use as is
          return pricePerUnit || 0;
        }
        if (tx.priceUnit === PriceUnit.PRICE_PER_GRAM) {
          // Per gram, convert to per kilogram (multiply by 1000)
          return (pricePerUnit || 0) * 1000;
        }
        if (tx.priceUnit === PriceUnit.PRICE_PER_BAHT) {
          // Per baht, convert to per kilogram (multiply by 65.6)
          return (pricePerUnit || 0) * 65.6;
        }
        return pricePerUnit || 0;
      }
      return pricePerUnit || 0;
    })();

    const displayPriceDifferencePerUnit = (() => {
      if (isSilverProduct) {
        // For silver products, determine conversion based on PriceUnit
        if (tx.priceUnit === PriceUnit.PRICE_PER_KG) {
          return priceDifferencePerUnit || 0;
        }
        if (tx.priceUnit === PriceUnit.PRICE_PER_GRAM) {
          return (priceDifferencePerUnit || 0) * 1000;
        }
        if (tx.priceUnit === PriceUnit.PRICE_PER_BAHT) {
          return (priceDifferencePerUnit || 0) * 65.6;
        }
        return priceDifferencePerUnit || 0;
      }
      return priceDifferencePerUnit || 0;
    })();

    const priceUnitLabel = isSilverProduct ? 'THB/กก.' : 'THB';

    return [
      {
        label: 'สถานะ',
        value: (
          <Label
            variant="soft"
            color={
              (status === TransactionStatus.SUCCESS && 'success') ||
              (status === TransactionStatus.PENDING && 'warning') ||
              (status === TransactionStatus.REJECTED && 'error') ||
              (status === TransactionStatus.CANCEL && 'info') ||
              (status === TransactionStatus.WAITING && 'info') ||
              'default'
            }
          >
            {txStatusLabel(status)}
          </Label>
        ),
      },
      {
        label: 'ประเภท',
        value: (
          <Typography
            variant="subtitle2"
            color={tx.type === TransactionType.BUY_POSITION ? 'success.main' : 'error'}
          >
            {txAction(tx.type)}
          </Typography>
        ),
      },
      { label: 'สินค้า', value: tx.product.name },
      {
        label: 'น้ำหนัก',
        value: `${fNumber(amount)} ${unitName || 'บาท'}${remainGoldAmount > -1 ? ` (คงเหลือ ${fNumber(remainGoldAmount)} ${unitName || 'บาท'})` : ''}`,
      },
      {
        label: 'ราคาต่อหน่วย',
        value: `${displayPricePerUnit ? fNumber(displayPricePerUnit) : '-'} ${priceUnitLabel}`,
      },
      {
        label: 'ราคาส่วนต่างต่อหน่วย',
        value: `${displayPriceDifferencePerUnit ? fNumber(displayPriceDifferencePerUnit) : '-'} ${priceUnitLabel}`,
      },
      // { label: 'ราคาปิด', value: `${fNumber(closePositionPricePerUnit || 0)} THB` },
      { label: 'ค่าธรรมเนียม', value: `${fNumber(fee || 0)} THB` },
      {
        label: 'ยอดรวม',
        value: `${tx.total ? fNumber(tx.total) : '-'} THB`,
      },
      { label: 'ผู้ใช้งาน', value: `${user?.firstName} ${user?.lastName}` },
      { label: 'วันที่ทำรายการ', value: fDate(createdAt, 'DD MMMM BBBB HH:mm น.') },
      ...(status === TransactionStatus.SUCCESS && !!paidAt
        ? [
            {
              label: 'วันที่โอน',
              value:
                status === TransactionStatus.SUCCESS && !!paidAt
                  ? fDate(paidAt, 'DD MMMM BBBB HH:mm น.')
                  : '-',
            },
          ]
        : []),
      ...([TransactionStatus.SUCCESS, TransactionStatus.REJECTED].includes(status) && !!paidAt
        ? [
            {
              label: `วันที่${statusAction(status)}`,
              value: fDate(updatedAt, 'DD MMMM BBBB HH:mm น.'),
            },
          ]
        : []),
      ...(approvedByUser
        ? [
            {
              label: `${statusAction(status)}โดย`,
              value: `${approvedByUser?.firstName} ${approvedByUser?.lastName}`,
            },
          ]
        : []),
      ...(paymentType
        ? [
            {
              label: 'วิธีการชําระเงิน',
              value: paymentTypeLabel(paymentType),
            },
          ]
        : []),
      ...(tx.branch?.name
        ? [
            {
              label: 'สาขาที่ดำเนินการ',
              value: tx.branch?.name || '-',
            },
          ]
        : []),
      ...(paymentType !== UserTransactionPaymentType.CREDIT && paymentImageUrl
        ? [
            {
              label: 'หลักฐานการโอนเงิน',
              value: <ImageWithPreview src={paymentImageUrl} alt="" />,
            },
          ]
        : []),
      ...(paymentVoucherNo
        ? [
            {
              label: 'เลขที่ใบสำคัญจ่าย',
              value: paymentVoucherNo,
            },
          ]
        : []),
      ...(paymentReceiptNo
        ? [
            {
              label: 'เลขที่ใบเสร็จ',
              value: paymentReceiptNo,
            },
          ]
        : []),
      ...(ticketCreatedAt
        ? [
            {
              label: 'วันที่ออกตั๋ว',
              value: fDate(ticketCreatedAt, 'DD MMMM BBBB'),
            },
          ]
        : []),
      ...(ticketNumber
        ? [
            {
              label: 'เลขที่ตั๋ว',
              value: ticketNumber,
            },
          ]
        : []),
      {
        label: 'หมายเหตุ',
        value: note || '-',
      },
    ];
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <Stack direction="row" alignItems="center" sx={{ p: 3 }}>
        <Typography
          color={tx.type === TransactionType.BUY_POSITION ? 'success.main' : 'error'}
          variant="h6"
          mx={0.5}
        >
          {txAction(tx.type)}
        </Typography>
        <Typography variant="h6">
          {tx.product.name} {tx.amount} {tx.unitName}
        </Typography>
      </Stack>

      <DialogContent>
        <InfoGrid infos={getInfo()} />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
